import { supabase } from '../lib/supabaseClient';
import { getCurrentAttempt, touchSession } from './session';
import { enqueue } from './logQueue';
import { newId } from './uuid';
import { currentStepTiming, countHint } from './stepTiming';

// =============================================================
// 実験ログの記録（RQ②: 演習の実施可能性／RQ③: 自己説明）
// 設計の正本：research/20261010_操作ログDB設計指針.md（2026-10-10 決定＝折衷案）
//
// - 研究の主データ（MUST）は専用の表へ：
//     logStepResponse()    … 設問ごとの回答（着色箇所・自由記述・選択肢・時間）→ step_responses / option_selections
//     logPartColorChange() … X-1 の着色の選び直し → part_color_changes
//     logOptionChange()    … X-2 の空欄の選び直し → option_changes
// - それ以外の細かい操作は logEvent() → experiment_logs。主データをここに二重に書かない。
// - どの行も「取り組み」（attempts。ログイン1回＝1行、ゲスト＝学習者番号0）を親に持つ。
//   取り組みの行は、その取り組みで最初に何かを記録するときに自動で先に積む。
// - 演習画面は setExerciseContext() で「いまどの演習の何問目の何ステップか」を知らせる。
//   logEvent はそれを自動で付け、主データの記録は文脈がないとき（デモ画面など）は送らない。
// - 送信は utils/logQueue.js（1本のキュー・作った順・重複＝保存済み）。
// - Supabase の環境変数が未設定なら何もしない（開発時にアプリを邪魔しない）。
// =============================================================

const SESSION_KEY = 'ex2026SessionId';
const SENT_ATTEMPTS_KEY = 'experimentLogAttempts';

let warnedNoSupabase = false;
let context = null;

// タブ単位のセッションID（同一タブの一連の操作を束ねる）
function getSessionId() {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

function enabled() {
  if (supabase) return true;
  if (!warnedNoSupabase) {
    console.warn('experimentLog: REACT_APP_SUPABASE_URL / REACT_APP_SUPABASE_ANON_KEY が未設定のためログ送信は無効です');
    warnedNoSupabase = true;
  }
  return false;
}

// いまの取り組みを返し、その attempts 行をまだ積んでいなければ先に積む（子の行より前に並ぶ）
function currentAttempt() {
  const attempt = getCurrentAttempt();
  if (!attempt) return null;
  let sent = [];
  try { sent = JSON.parse(localStorage.getItem(SENT_ATTEMPTS_KEY) || '[]'); } catch (_) { sent = []; }
  if (!sent.includes(attempt.attemptId)) {
    enqueue('attempts', {
      attempt_id: attempt.attemptId,
      learner_id: attempt.learnerId,
      started_at: new Date(attempt.startedAt || Date.now()).toISOString(),
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    });
    try { localStorage.setItem(SENT_ATTEMPTS_KEY, JSON.stringify([...sent, attempt.attemptId].slice(-50))); } catch (_) { /* 重複して積んでも保存済み扱いになる */ }
  }
  return attempt;
}

/**
 * 演習画面の「いま」を知らせる（演習ルートが問×ステップを開くたびに呼ぶ。useExerciseStepLog）。
 * @param {{exercise: 'shape'|'gaze', questionNumber: number, step: 1|2, glbPath: string}} ctx
 *   step は 1＝X-1（部品選択）、2＝X-2（シミュレーション）。
 *   所要時間は utils/stepTiming.js が数える（最初に開いた時刻から）。shownAt はそれが使えないときの予備。
 */
export function setExerciseContext(ctx) {
  context = { ...ctx, shownAt: Date.now(), hintCount: 0 };
}

/**
 * ヒント（おかしさが分かる視点で見る）を押したことを記録する（experiment_logs の hint_used）。
 * そのステップを最初に開いてから押すまでの時間（elapsedMs。所要時間 duration_ms と同じ数え方）、
 * そのうち画面が出ていた時間（activeMs）、そのステップで何回目か（hintIndex。1＝初めて）を付ける。
 * 初めて押すまでの時間で、ヒントに頼る前にどれだけ自分で3Dモデルを触って探したかを見る。
 * 回数はタブで開き直してもリロードしても続きから数える。
 */
export function logHintUsed(data = {}) {
  const timing = {};
  const t = currentStepTiming();
  if (t) {
    const n = countHint();
    timing.elapsedMs = t.durationMs;
    timing.activeMs = t.activeMs;
    timing.hintIndex = n;
    timing.isFirstHint = n === 1;
  } else if (context) {
    context.hintCount += 1;
    timing.elapsedMs = Date.now() - context.shownAt;
    timing.hintIndex = context.hintCount;
    timing.isFirstHint = context.hintCount === 1;
  }
  logEvent('hint_used', { ...data, ...timing });
}

/** 演習画面を離れたときに呼ぶ（デモ画面の操作に演習の文脈が付かないようにする）。 */
export function clearExerciseContext() {
  context = null;
}

/**
 * 細かい操作を1件記録する（experiment_logs）。
 * @param {string} eventType 例: 'login' | 'logout' | 'guest_start' | 'exercise_step_view' | 'hint_used'（logHintUsed 経由）
 *   | 'shape_step_change' | 'gaze_match_start' | 'gaze_reset' | 'selfexp_template_open'
 * @param {object} data questionNumber / glbPath は専用カラム（省くと演習の文脈から補う）、残りは payload に入る
 */
export function logEvent(eventType, data = {}) {
  if (!enabled()) return;
  // 操作があった＝セッションを延ばす（演習中にタイムアウトさせない）
  touchSession();

  const { questionNumber, glbPath, ...payload } = data;
  const attempt = currentAttempt();
  enqueue('experiment_logs', {
    event_id: newId(),
    attempt_id: attempt ? attempt.attemptId : null,
    user_id: attempt ? attempt.learnerId : null,
    session_id: getSessionId(),
    event_type: eventType,
    route: window.location.pathname,
    exercise: context ? context.exercise : null,
    step: context ? context.step : null,
    question_number: questionNumber ?? (context ? context.questionNumber : null),
    glb_path: glbPath ?? (context ? context.glbPath : null),
    payload,
    client_ts: new Date().toISOString(),
  });
}

// 主データの記録に使う「誰の・どの設問か」。演習の文脈と取り組みがそろわなければ記録しない
function questionKey() {
  if (!enabled() || !context) return null;
  touchSession();
  const attempt = currentAttempt();
  if (!attempt) return null;
  return {
    attempt_id: attempt.attemptId,
    exercise: context.exercise,
    question_number: context.questionNumber,
    glb_path: context.glbPath,
  };
}

/**
 * 設問×ステップの回答を記録する（step_responses ＋ 選択式なら option_selections）。
 * @returns {boolean} 記録したか（演習の文脈がないデモ画面などでは false）
 */
export function logStepResponse({ selectedPart, selfExplanation, answerMode, templateUsed, prompt, selectedOptions }) {
  const key = questionKey();
  if (!key) return false;
  const now = Date.now();
  const responseId = newId();
  // 所要時間：最初に開いてから提出まで（duration_ms）と、そのうち画面が出ていた時間（active_ms）
  const timing = currentStepTiming(now);
  const shownAt = timing ? timing.firstShownAt : context.shownAt;
  enqueue('step_responses', {
    response_id: responseId,
    ...key,
    step: context.step,
    selected_part: selectedPart || null,
    self_explanation: selfExplanation ?? null,
    answer_mode: answerMode || null,
    template_used: Boolean(templateUsed),
    prompt: prompt || null,
    shown_at: new Date(shownAt).toISOString(),
    submitted_at: new Date(now).toISOString(),
    duration_ms: Math.max(0, now - shownAt),
    active_ms: timing ? timing.activeMs : Math.max(0, now - context.shownAt),
  });
  Object.entries(selectedOptions || {}).forEach(([slot, value]) => {
    if (value) enqueue('option_selections', { response_id: responseId, slot, value });
  });
  return true;
}

/** X-1 で着色する部品を選んだ・選び直した（未選択に戻したときは part が空）。 */
export function logPartColorChange(part) {
  const key = questionKey();
  if (!key) return;
  enqueue('part_color_changes', { id: newId(), ...key, part: part || null, changed_at: new Date().toISOString() });
}

/** X-2 の空欄で語を選んだ・選び直した。 */
export function logOptionChange(slot, value, previous) {
  const key = questionKey();
  if (!key) return;
  enqueue('option_changes', {
    id: newId(), ...key, slot, value: value || null, previous: previous || null, changed_at: new Date().toISOString(),
  });
}
